import * as Sentry from "@sentry/react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useLoaderData,
} from "react-router";
import { useEffect, useState } from "react";

import type { Route } from "./+types/root";
import { logRequest } from "./utils/requestLogger";
import { getUserFromRequest } from "./auth.server";
import { fireRegistroConversion } from "./utils/adsConversions";
import { trackGa } from "./utils/analytics";
import { Footer } from "./components/layout/Footer";
import { ScrollToTop } from "./components/ui/ScrollToTop";
import soraFontUrl from "@fontsource-variable/sora/files/sora-latin-wght-normal.woff2?url";
import "./app.css";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID as string | undefined;
const GTAG_ID = GA_ID ?? GOOGLE_ADS_ID;

export const links: Route.LinksFunction = () => [
  // Precarga la fuente del título (elemento LCP en la portada) para que el texto
  // se asiente en Sora sin esperar a que el CSS descubra la @font-face.
  {
    rel: "preload",
    href: soraFontUrl,
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  {
    rel: "icon",
    type: "image/svg+xml",
    href: "/favicon-light.svg",
    media: "(prefers-color-scheme: light)",
  },
  {
    rel: "icon",
    type: "image/svg+xml",
    href: "/favicon-dark.svg",
    media: "(prefers-color-scheme: dark)",
  },
  { rel: "icon", href: "/favicon.ico" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {GTAG_ID && (
          <>
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`} />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${GA_ID ? `gtag('config','${GA_ID}',{send_page_view:false});` : ""}${GOOGLE_ADS_ID ? `gtag('config','${GOOGLE_ADS_ID}');` : ""}`,
              }}
            />
          </>
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": ["Organization", "EducationalOrganization"],
                  "@id": "https://www.escuelavial.cl/#organization",
                  "name": "Escuela Vial",
                  "url": "https://www.escuelavial.cl",
                  "logo": "https://www.escuelavial.cl/logo.svg",
                  "image": "https://www.escuelavial.cl/og-image.png",
                  "description":
                    "Plataforma de preparación para el examen teórico de licencia de conducir en Chile. Preguntas basadas en fuentes oficiales de CONASET, simulacros cronometrados y estadísticas de progreso.",
                  "areaServed": "CL",
                  "inLanguage": "es-CL",
                },
                {
                  "@type": "WebSite",
                  "@id": "https://www.escuelavial.cl/#website",
                  "url": "https://www.escuelavial.cl",
                  "name": "Escuela Vial",
                  "inLanguage": "es-CL",
                  "publisher": { "@id": "https://www.escuelavial.cl/#organization" },
                },
                {
                  "@type": "WebApplication",
                  "@id": "https://www.escuelavial.cl/#webapp",
                  "name": "Escuela Vial",
                  "url": "https://www.escuelavial.cl",
                  "applicationCategory": "EducationalApplication",
                  "operatingSystem": "Web",
                  "inLanguage": "es-CL",
                  "provider": { "@id": "https://www.escuelavial.cl/#organization" },
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "CLP",
                    "description": "Práctica gratuita para comenzar",
                  },
                },
              ],
            }),
          }}
        />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export async function loader({ request }: { request: Request }) {
  logRequest(request, "root");
  // Las URLs públicas son siempre en minúsculas: /materiales/audiolibros/clase-B → 301 a clase-b.
  // API, admin y test quedan fuera porque sus parámetros pueden distinguir mayúsculas.
  const url = new URL(request.url);
  if (/[A-Z]/.test(url.pathname) && !/^\/(api|admin|test)\//.test(url.pathname)) {
    url.pathname = url.pathname.toLowerCase();
    throw redirect(url.toString(), 301);
  }
  const user = getUserFromRequest(request);
  return { user: user ? { id: user.id, email: user.email, isAdmin: user.rol === "ADMIN" } : null };
}

export default function App() {
  const location = useLocation();
  const data = useLoaderData<typeof loader>();
  const hideFooter = location.pathname.startsWith("/test/sesion/") || /^\/test\/\d+\/resultado/.test(location.pathname);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Identifica al usuario autenticado en Sentry (y lo limpia al cerrar sesión).
  useEffect(() => {
    const user = data?.user;
    if (user) {
      Sentry.setUser({ id: String(user.id), email: user.email, username: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [data?.user?.id, data?.user?.email]);

  // Administradores = tráfico interno: GA lo excluye con el filtro de datos "Tráfico interno".
  // `set` aplica traffic_type a todos los eventos siguientes (page_view, sign_up, purchase…).
  // Va antes del efecto de page_view: los efectos corren en orden de declaración.
  const isAdmin = Boolean(data?.user?.isAdmin);
  useEffect(() => {
    if (GA_ID && typeof (window as any).gtag === "function") {
      (window as any).gtag("set", { traffic_type: isAdmin ? "internal" : undefined });
    }
  }, [isAdmin]);

  useEffect(() => {
    if (GA_ID && typeof (window as any).gtag === "function") {
      (window as any).gtag("event", "page_view", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  // Registro con Google: registro.tsx redirige con ?nuevo_registro=<id> cuando se
  // creó una cuenta nueva. Disparamos la conversión y limpiamos el parámetro de la URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nuevo = params.get("nuevo_registro");
    if (!nuevo) return;
    fireRegistroConversion(`g${nuevo}`);
    trackGa("sign_up", { method: "google" }, `g${nuevo}`);
    params.delete("nuevo_registro");
    const qs = params.toString();
    const cleanUrl = location.pathname + (qs ? `?${qs}` : "") + location.hash;
    window.history.replaceState(window.history.state, "", cleanUrl);
  }, [location.pathname, location.search, location.hash]);

  // Enlace de referido (?ref=CODIGO): se guarda 90 días en una cookie que registro/login envían
  // al backend al crear la cuenta (ver getCodigoReferido). Se quita el parámetro de la URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref === null) return;
    const codigo = ref.trim().toUpperCase();
    if (/^[A-Z0-9]{4,20}$/.test(codigo)) {
      document.cookie = `ev_ref=${codigo}; Path=/; Max-Age=${90 * 24 * 3600}; SameSite=Lax`;
    }
    params.delete("ref");
    const qs = params.toString();
    window.history.replaceState(window.history.state, "", location.pathname + (qs ? `?${qs}` : "") + location.hash);
  }, [location.pathname, location.search, location.hash]);

  // Intercepta todas las respuestas 403 del backend y avisa que la sesión venció
  useEffect(() => {
    const original = window.fetch;
    window.fetch = async (...args) => {
      const response = await original(...args);
      if (response.status === 401 || response.status === 403) {
        setSessionExpired(true);
        setTimeout(() => {
          window.location.href = "/login";
        }, 2500);
      }
      return response;
    };
    return () => {
      window.fetch = original;
    };
  }, []);

  return (
    <>
      <Outlet />
      {!hideFooter && <Footer />}
      <ScrollToTop />
      {sessionExpired && <SessionExpiredOverlay />}
    </>
  );
}

function SessionExpiredOverlay() {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-white px-8 py-8 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <svg
            className="h-7 w-7 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900">Sesión vencida</h2>
        <p className="mt-2 text-sm text-gray-500">
          Tu sesión ha expirado. Redirigiendo al inicio de sesión…
        </p>
        <div className="mt-5 h-1 w-full overflow-hidden rounded-full bg-gray-100">
          <div className="h-1 animate-[shrink_2.5s_linear_forwards] rounded-full bg-amber-400" />
        </div>
      </div>
    </div>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  if (!is404) {
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  }

  let stack: string | undefined;
  if (!isRouteErrorResponse(error) && import.meta.env.DEV && error instanceof Error) {
    stack = error.stack;
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[#F7F7F7] px-4 py-16 text-center">
      <div className="text-5xl font-extrabold text-rose-600">{is404 ? "404" : "Error"}</div>
      <h1 className="mt-4 text-2xl font-bold text-gray-900">
        {is404 ? "Página no encontrada" : "Algo salió mal"}
      </h1>
      <p className="mt-2 max-w-sm text-sm text-gray-600">
        {is404
          ? "La página que buscas no existe o fue movida."
          : "Ocurrió un error inesperado. Por favor intenta de nuevo."}
      </p>
      <a
        href="/"
        className="mt-8 inline-flex items-center rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-rose-700"
      >
        Volver al inicio
      </a>
      {stack && (
        <pre className="mt-8 w-full max-w-2xl overflow-x-auto rounded-xl bg-gray-900 p-4 text-left text-xs text-gray-300">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
